import {
  ImageProcessingBatch,
  isLockedBatchItemStatus,
} from 'src/redux/api/inventory/imageProcessingBatches';

export const getLockedMediaIdsForVehicle = (
  openBatches: ImageProcessingBatch[],
  vehicleId?: number
): number[] => {
  if (!vehicleId) {
    return [];
  }

  return openBatches
    .filter((batch) => batch.vehicle_id === vehicleId)
    .flatMap((batch) =>
      batch.items.filter((item) => isLockedBatchItemStatus(item.status)).map((item) => item.media_id)
    );
};

export const getReadyBatchItems = (batch: ImageProcessingBatch) =>
  batch.items.filter((item) => item.status === 'ready');

/**
 * A canceled batch keeps its already produced results until they expire, so reviewability is
 * driven by the items rather than by the batch status.
 */
export const isReviewableBatch = (batch: ImageProcessingBatch) =>
  getReadyBatchItems(batch).length > 0;

export const getVehicleDisplayInfo = (batch: ImageProcessingBatch) => ({
  title: batch.vehicle?.title || '',
  thumbnail: batch.vehicle?.thumbnail || '',
  vin: batch.vehicle?.vin?.toUpperCase(),
});

/**
 * Vehicle-scoped endpoints omit the vehicle summary, so carry over the one we already have
 * instead of blanking the toast title and the review header on every update.
 */
export const withPreservedVehicle = (
  incoming: ImageProcessingBatch,
  existing?: ImageProcessingBatch | null
): ImageProcessingBatch =>
  incoming.vehicle || !existing?.vehicle ? incoming : { ...incoming, vehicle: existing.vehicle };

/**
 * Returns the API message for expected validation failures (for example, selecting only photos
 * that were already enhanced) so they can be shown as a toast instead of a generic error report.
 */
export const getValidationErrorMessage = (error: unknown): string | undefined => {
  const { status, data } = (error ?? {}) as { status?: number; data?: { message?: unknown } };

  if (typeof status !== 'number' || status < 400 || status >= 500) {
    return undefined;
  }

  return typeof data?.message === 'string' ? data.message : undefined;
};
